import React, { useState, useEffect } from 'react';
import { Card, Statistic, Row, Col, Progress, DatePicker, Select } from 'antd';
import { DropboxOutlined, LineChartOutlined } from '@ant-design/icons';
import IntlMessage from "../../../../components/IntlMessage/IntlMessage";
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const WaterConsumption = () => {
    const [consumptionData, setConsumptionData] = useState({
        totalFlow: 1250.5,
        avgVelocity: 2.8,
        efficiency: 87,
        dailyConsumption: 45.2,
        monthlyConsumption: 1356.0,
        yearlyConsumption: 16272.0
    });
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'days'), dayjs()]);
    const [period, setPeriod] = useState('week');

    useEffect(() => {
        fetchConsumptionData();
    }, [dateRange, period]);

    const fetchConsumptionData = async () => {
        setLoading(true);
        try {
            // TODO: Implement API call to fetch consumption data
            // This would normally fetch data based on dateRange and period
            console.log('Fetching consumption data for period:', period, dateRange);
            
            // Simulate different data based on period
            const mockData = {
                week: { totalFlow: 1250.5, avgVelocity: 2.8, efficiency: 87 },
                month: { totalFlow: 5420.3, avgVelocity: 2.6, efficiency: 89 },
                year: { totalFlow: 65043.8, avgVelocity: 2.7, efficiency: 85 }
            };
            
            setConsumptionData({
                ...consumptionData,
                ...mockData[period]
            });
        } catch (error) {
            console.error('Failed to fetch consumption data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getEfficiencyColor = (efficiency) => {
        if (efficiency >= 90) return '#52c41a';
        if (efficiency >= 75) return '#faad14';
        return '#ff4d4f';
    };

    return (
        <Card 
            title={
                <>
                    <DropboxOutlined style={{ marginRight: 8 }} />
                    <IntlMessage id={'dashboard.admin.waterConsumption.title'} fallback={'Water Consumption Analytics'} />
                </>
            }
            style={{ height: '100%' }}
            extra={
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <Select
                        value={period}
                        onChange={setPeriod}
                        style={{ width: 120 }}
                    >
                        <Option value="week"><IntlMessage id={'week'} fallback={'Week'} /></Option>
                        <Option value="month"><IntlMessage id={'month'} fallback={'Month'} /></Option>
                        <Option value="year"><IntlMessage id={'year'} fallback={'Year'} /></Option>
                    </Select>
                    <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        format="DD.MM.YYYY"
                    />
                </div>
            }
        >
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                    <Statistic
                        title={<IntlMessage id={'dashboard.admin.waterConsumption.totalFlow'} fallback={'Total Flow'} />}
                        value={consumptionData.totalFlow}
                        suffix="m³"
                        loading={loading}
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Statistic
                        title={<IntlMessage id={'dashboard.admin.waterConsumption.avgVelocity'} fallback={'Avg Velocity'} />}
                        value={consumptionData.avgVelocity}
                        suffix="m/s"
                        precision={1}
                        loading={loading}
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div>
                        <div style={{ marginBottom: 8, fontSize: '14px', color: '#666' }}>
                            <IntlMessage id={'dashboard.admin.waterConsumption.efficiency'} fallback={'System Efficiency'} />
                        </div>
                        <Progress
                            percent={consumptionData.efficiency}
                            strokeColor={getEfficiencyColor(consumptionData.efficiency)}
                            format={percent => `${percent}%`}
                        />
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Statistic
                        title={<IntlMessage id={'dashboard.admin.waterConsumption.daily'} fallback={'Daily Consumption'} />}
                        value={consumptionData.dailyConsumption}
                        suffix="m³"
                        precision={1}
                        loading={loading}
                    />
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} md={12}>
                    <Card size="small" title={<IntlMessage id={'dashboard.admin.waterConsumption.monthly'} fallback={'Monthly Summary'} />}>
                        <Statistic
                            value={consumptionData.monthlyConsumption}
                            suffix="m³"
                            precision={1}
                            prefix={<LineChartOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card size="small" title={<IntlMessage id={'dashboard.admin.waterConsumption.yearly'} fallback={'Yearly Summary'} />}>
                        <Statistic
                            value={consumptionData.yearlyConsumption}
                            suffix="m³"
                            precision={1}
                            prefix={<LineChartOutlined />}
                        />
                    </Card>
                </Col>
            </Row>
        </Card>
    );
};

export default WaterConsumption;